import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { AppState } from 'src/app/core/app.state';
import { SharedService } from '../services/shared.services';
import { catchError, finalize, map, of, switchMap, tap } from 'rxjs';
import { SetLoading } from './shared.actions';
import * as sharedsActions from './shared.actions';
import { IBrand } from '../models/brands.models';
import { APIResponse } from 'src/app/core/models/api-response';
import { ITag } from '../models/tags.models';
import { ICategory } from '../models/catagories.models';
import { DateLocaleService } from '../services/date-locale.service';
import { IObjectCustomFields } from '../models/custom-fields.models';
import { CommonService } from '../common.service';
import { TranslateService } from '@ngx-translate/core';

@Injectable()
export class SharedEffects {
  private actions$ = inject(Actions);
  private sharedService = inject(SharedService);
  private store = inject<Store<AppState>>(Store);
  private dateLocaleService = inject(DateLocaleService);
  private commonService = inject(CommonService);
  private translateService = inject(TranslateService);

  // fetch All Brands
  FetchAllBrandsEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(sharedsActions.FetchAllBrands),
      switchMap(() =>
        this.sharedService.listAllBrands().pipe(
          map((res: APIResponse<IBrand>) =>
            sharedsActions.FetchAllBrandsSuccess({ brands: res.data })
          ),
          tap(() => {
            this.store.dispatch(SetLoading({ isAppLoading: false }));
          }),
          catchError(() =>
            of(sharedsActions.FetchAllBrandsError()).pipe(
              tap(() => {
                this.store.dispatch(SetLoading({ isAppLoading: false }));
              })
            )
          )
        )
      ),
      finalize(() => {
        this.store.dispatch(SetLoading({ isAppLoading: false }));
      })
    )
  );

  // fetch All Tags
  FetchAllTagsEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(sharedsActions.FetchAllTags),
      switchMap(() =>
        this.sharedService.listAllTags().pipe(
          map((res: APIResponse<ITag>) => sharedsActions.FetchAllTagsSuccess({ tags: res.data })),
          tap(() => {
            this.store.dispatch(SetLoading({ isAppLoading: false }));
          }),
          catchError(() =>
            of(sharedsActions.FetchAllTagsError()).pipe(
              tap(() => {
                this.store.dispatch(SetLoading({ isAppLoading: false }));
              })
            )
          )
        )
      ),
      finalize(() => {
        this.store.dispatch(SetLoading({ isAppLoading: false }));
      })
    )
  );

  // fetch All Categories
  FetchAllCategoriesEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(sharedsActions.FetchAllCategories),
      switchMap(() =>
        this.sharedService.listAllCategories().pipe(
          map((res: APIResponse<ICategory>) =>
            sharedsActions.FetchAllCategoriesSuccess({ categories: res })
          ),
          tap(() => {
            this.store.dispatch(SetLoading({ isAppLoading: false }));
          }),
          catchError(() =>
            of(sharedsActions.FetchAllCategoriesError()).pipe(
              tap(() => {
                this.store.dispatch(SetLoading({ isAppLoading: false }));
              })
            )
          )
        )
      ),
      finalize(() => {
        this.store.dispatch(SetLoading({ isAppLoading: false }));
      })
    )
  );

  // Set Language Effect
  SetLanguageEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(sharedsActions.SetLanguage),
      tap((action) => {
        this.dateLocaleService.setLocale(action.language);
      })
    ),
    { dispatch: false }
  );

    // Get Object Custom Fields
    GetObjectCustomFieldsEffect$ = createEffect(() =>
      this.actions$.pipe(
        ofType(sharedsActions.GetObjectCustomFields),
        tap(() => {
          this.store.dispatch(sharedsActions.SetCustomFieldsLoader({ isLoading: true }));
        }),
        switchMap((action) =>
          this.sharedService.getObjectCustomFields(action.params).pipe(
            map((res: APIResponse<IObjectCustomFields>) => sharedsActions.GetObjectCustomFieldsSuccess({ objectCustomFields: res.object! })),
            catchError(() => of(sharedsActions.GetObjectCustomFieldsError())),
            finalize(() => {
              this.store.dispatch(sharedsActions.SetCustomFieldsLoader({ isLoading: false }));
            })
          )
        )
      )
    );
  
    // Update Object Custom Fields values
    UpdateObjectCustomFieldsEffect$ = createEffect(() =>
      this.actions$.pipe(
        ofType(sharedsActions.UpdateObjectCustomFields),
        tap(() => {
          this.store.dispatch(sharedsActions.SetCustomFieldsLoader({ isLoading: true }));
        }),
        switchMap((action) =>
          this.sharedService.updateObjectCustomFields(action.params, action.values).pipe(
            map((res: APIResponse<IObjectCustomFields>) => {
              return sharedsActions.UpdateObjectCustomFieldsSuccess({ objectCustomFields: res.object! });
            }),
            tap(() => {
              this.commonService.showToast('Custom fields saved', 'SUCCESS');
              this.store.dispatch(
                sharedsActions.GetObjectCustomFields({
                  params: action.params,
                })
              );
            }),
            catchError(() =>
              of(sharedsActions.UpdateObjectCustomFieldsError()),
            ),
            finalize(() => {
              this.store.dispatch(sharedsActions.SetCustomFieldsLoader({ isLoading: false }));
            })
          )
        )
      )
    );
}
